import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import SearchBox from '../../common/SearchBox';
import {
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
  TableBody,
  TablePagination
} from '@material-ui/core';
import PageTemplateCardOver from '../../common/page-template/PageTemplateCardOver';
import {
  useQueryParam,
  NumberParam,
  StringParam,
  withDefault
} from 'use-query-params';
import { useHistory } from 'react-router-dom';
import { Scar } from './Scar.interface';
import { useGetScarList } from './Scar.hook';
import ScarCreateDialog from './ScarCreateDialog';
import { formatDate } from '../../common/formatter';
import UserLink from '../users/components/UserLink';
import TaskStatusLabel from '../../common/TaskStatusLabel';

const ScarPage: React.FC = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [filter = '', setFilter] = useQueryParam('filter', StringParam);
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 0));
  const [limit, setLimit] = useQueryParam(
    'limit',
    withDefault(NumberParam, 100)
  );
  const [order, setOrder] = useQueryParam('order', StringParam);
  const [orderDirection, setOrderDirection] = useQueryParam(
    'direction',
    StringParam
  );
  const history = useHistory();

  const [{ data: scarList }, get] = useGetScarList();

  const getScarListWithParams = useCallback(() => {
    const params = {
      search: filter,
      page: page,
      limit: limit,
      order: (order && order + '-' + orderDirection) || 'id-DESC'
    };
    get(params);
  }, [page, limit, order, filter, get, orderDirection]);

  useEffect(() => {
    getScarListWithParams();
  }, [getScarListWithParams]);

  const handleSort = (column: string) => {
    setOrder(column);
    setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && orderDirection === 'DESC' ? 'desc' : 'asc';
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(+newPage);
  };

  const handleChangeRowPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setLimit(+event.target.value);
    setPage(0);
  };

  const close = () => {
    setShowPrompt(false);
    getScarListWithParams();
  };

  return (
    <PageTemplate>
      <PageTemplateHeader>
        <h2>SCAR</h2>
        <SearchBox value={filter} onSearch={setFilter} />
        <PageTemplateHeaderActions>
          <Button
            variant='contained'
            color='primary'
            onClick={() => setShowPrompt(true)}
          >
            Add SCAR
          </Button>
        </PageTemplateHeaderActions>
      </PageTemplateHeader>

      {showPrompt && (
        <ScarCreateDialog
          open={showPrompt}
          //One for built-in backdrop click, one for cancel button click
          onClose={close}
          onCancel={close}
          onCreate={close}
        />
      )}
      <PageTemplateCardOver>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={order === 'id'}
                    direction={getSortDirection('id')}
                    onClick={() => handleSort('id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'companyName'}
                    direction={getSortDirection('companyName')}
                    onClick={() => handleSort('companyName')}
                  >
                    Supplier Company Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'createdBy'}
                    direction={getSortDirection('createdBy')}
                    onClick={() => handleSort('createdBy')}
                  >
                    Created By
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'created'}
                    direction={getSortDirection('created')}
                    onClick={() => handleSort('created')}
                  >
                    Created Date
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {scarList &&
                scarList.data.map((scar: Scar) => (
                  <TableRow
                    hover
                    key={scar.id}
                    onClick={() =>
                      history.push(`/portal/scar/task/${scar.taskId}`)
                    }
                  >
                    <TableCell>SCAR-{scar.id}</TableCell>
                    <TableCell>{scar.supplier?.companyName}</TableCell>
                    <TableCell>{scar.description}</TableCell>
                    <TableCell>
                      <TaskStatusLabel task={scar.task} />
                    </TableCell>
                    <TableCell>
                      <UserLink
                        type='name'
                        user={scar.createdBy}
                        disableLink={true}
                      />
                    </TableCell>
                    <TableCell>{formatDate(scar.created)}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component='div'
          count={scarList?.total || 0}
          rowsPerPage={limit}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowPerPage}
        />
      </PageTemplateCardOver>
    </PageTemplate>
  );
};
export default ScarPage;
